import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkUpdateAutoFulfill } from 'src/components/common/BulkActions/BulkUpdateAutoFulfillAction';
import { useBulkUpdateAutoFulfillPermissions } from 'src/components/common/BulkActions/BulkUpdateAutoFulfillAction/useBulkUpdateAutoFulfillPermissions';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkUpdateAutoFulfillDialog } from 'src/dialogs/BulkEdits/BulkUpdateAutoFulfillDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useAnyMarketplaceHandledByProcessor } from 'src/hooks/useMarketplaceHandledByProcessor';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  Feature,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_AUTOFULFILL_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';

export const useBulkUpdateAutoFulfillAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasEditAutoFulfillPermissions } = useBulkUpdateAutoFulfillPermissions(
    {
      entityType: ActionOutboxEntityType.Listing,
    }
  );
  const hasAutoFulfillFeature = useUserHasFeature(Feature.AutoFulfillSetting);
  const anyMkpHandledByProcessor = useAnyMarketplaceHandledByProcessor();

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `listing-autofulfill-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_AUTOFULFILL_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkUpdateAutoFulfillDialog,
    closeDialog: closeBulkUpdateAutofulfillDialog,
  } = useDialog(DialogId.BulkUpdateAutoFulfill, BulkUpdateAutoFulfillDialog);

  const onSubmit = useCallback(
    async (
      isNoFulfill: boolean | null,
      updateSalesOfListings: boolean,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateIsNoFulfillPreview(
              filterQueryWithEventIds,
              updateSalesOfListings
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateIsNoFulfillPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (isNoFulfill != null) {
        await tryInvokeApi(
          async () => {
            const listingClient = new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const succeeded = await listingClient.bulkUpdateIsNoFulfill(
              preview!.preview,
              updateKey,
              isNoFulfill,
              updateSalesOfListings,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkUpdateAutofulfillDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateIsNoFulfill', error, {
              trackErrorData: { isNoFulfill, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkUpdateAutofulfillDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onChangeClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }

      openBulkUpdateAutoFulfillDialog({
        updateKey,
        onOkay: onSubmit,
        entityType: ActionOutboxEntityType.Listing,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkUpdateAutofulfillDialog,
      });
    },
    [
      event,
      openBulkUpdateAutoFulfillDialog,
      updateKey,
      onSubmit,
      closeBulkUpdateAutofulfillDialog,
      setListItemExpansion,
      setIsLoading,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkUpdateAutoFulfillDialog({
      updateKey,
      onOkay: onSubmit,
      entityType: ActionOutboxEntityType.Listing,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkUpdateAutofulfillDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditAutoFulfill,
        onSelect: openDialogStableRef,
      },
      disabled:
        !anyMkpHandledByProcessor ||
        !hasEditAutoFulfillPermissions ||
        !hasAutoFulfillFeature,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditAutoFulfill,
        disabled:
          !anyMkpHandledByProcessor ||
          !hasEditAutoFulfillPermissions ||
          !hasAutoFulfillFeature,
      });
    };
  }, [
    anyMkpHandledByProcessor,
    hasAutoFulfillFeature,
    hasEditAutoFulfillPermissions,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (
      !anyMkpHandledByProcessor ||
      !hasEditAutoFulfillPermissions ||
      !hasAutoFulfillFeature
    ) {
      return [];
    }

    return [
      {
        disabled: isLoading,
        onClick: onChangeClick,
        title: ContentId.UpdateAutoFulfill,
      },
    ];
  }, [
    anyMkpHandledByProcessor,
    hasEditAutoFulfillPermissions,
    hasAutoFulfillFeature,
    isLoading,
    onChangeClick,
  ]);

  return {
    dropDown: (
      <LaunchBulkUpdateAutoFulfill
        key="UpdateAutoFulfill"
        onClick={onChangeClick}
        disabled={isLoading}
        entityType={ActionOutboxEntityType.Listing}
      />
    ),
    actionItems,
  };
};
